/**
 * CockpitHome — Stitch-faithful Executive Dashboard.
 *
 * Matches the Stitch "Full Frame | Cockpit Dashboard" output:
 * - Bento metric cards with Material Symbols + ambient shadows
 * - Production roadmap with visual progress bars
 * - Activity feed with icon circles
 * - Right sidebar: agenda/crew + AI insight card
 */
import React, { useCallback, useEffect } from 'react';
import { Box, Typography, CircularProgress, LinearProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import toast from 'react-hot-toast';
import useCockpitData from './useCockpitData';
import Shortcuts from './Shortcuts';
import { authFetch } from '../../utils/authHelpers';
import { BACKEND_URL } from '../../utils/apiConfig';
import { getWeekNumber } from '../../utils/weekUtils';
import { isDagstaatTaak } from '../../utils/taakStatus';
import { biedOngedaanAan, registreerOngedaanSneltoets } from '../../utils/ongedaan';

/* ── Stitch Design Tokens ──────────────────────────────────────────────────── */
const T = {
  primary:            '#526075',
  primaryDim:         '#465469',
  tertiary:           '#635983',
  tertiaryDim:        '#574d76',
  tertiaryContainer:  '#dbcdff',
  bg:                 '#f8f9fa',
  surface:            '#ffffff',
  surfaceLow:         '#f1f4f5',
  surfaceHigh:        '#e5e9eb',
  text:               '#2d3335',
  secondary:          '#556071',
  outline:            '#767c7e',
  outlineVariant:     '#adb3b5',
  primaryContainer:   '#d5e3fd',
  emerald:            '#10b981',
  emeraldBg:          '#ecfdf5',
  error:              '#a83836',
  shadow:             '0 10px 30px rgba(45,51,53,0.06)',
  font:               '"Manrope", system-ui, sans-serif',
  fontBody:           '"Inter", system-ui, sans-serif',
};

/** Material Symbols icon helper */
const Icon = ({ name, fill, size = 24, sx = {} }) => (
  <span
    className="material-symbols-outlined"
    style={{
      fontSize: size,
      fontVariationSettings: `'FILL' ${fill ? 1 : 0}, 'wght' 400, 'GRAD' 0, 'opsz' ${size}`,
      ...sx,
    }}
  >
    {name}
  </span>
);

/* ── Helpers ───────────────────────────────────────────────────────────────── */
const greet = () => {
  const h = new Date().getHours();
  return h < 6 ? 'Goedenacht' : h < 12 ? 'Goedemorgen' : h < 18 ? 'Goedemiddag' : 'Goedenavond';
};
const fmtWeekday = (s) => s ? new Date(s).toLocaleDateString('nl-NL', { weekday: 'short', day: 'numeric', month: 'short' }).replace('.', '') : '';
const dUntil = (s) => {
  if (!s) return null;
  const d = new Date(s), t = new Date();
  t.setHours(0,0,0,0); d.setHours(0,0,0,0);
  return Math.ceil((d - t) / 864e5);
};

/* ═══════════════════════════════════════════════════════════════════════════ */
export default function CockpitHome() {
  const { accounts } = useMsal();
  const navigate = useNavigate();
  const userEmail = accounts?.[0]?.username || '';
  const userName = accounts?.[0]?.name || '';
  const {
    loading, voornaam, todayTasks, weekTasks, overdueTasks,
    activeProjects, meetings, deadlines, draaidagen, feedback, attention, refresh,
  } = useCockpitData(userEmail, userName);

  useEffect(() => registreerOngedaanSneltoets(), []);

  const toggle = useCallback(async (task) => {
    const done = !task.f.Afgerond;
    const vorigePrio = task.f.PrioNr || (done ? 'Geen prio' : 'Afgerond');
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/tasks/${task.id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ PrioNr: done ? 'Afgerond' : 'Geen prio', Afgerond: done }),
      });
      if (!r.ok) throw new Error();
      biedOngedaanAan(done ? 'Afgerond' : 'Heropend', async () => {
        const t = await authFetch(`${BACKEND_URL}/Api/tasks/${task.id}`, {
          method: 'PATCH', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ PrioNr: vorigePrio, Afgerond: !done }),
        });
        if (!t.ok) throw new Error('Terugzetten mislukt');
        refresh();
      });
      refresh();
    } catch { toast.error('Kon taak niet bijwerken'); }
  }, [refresh]);

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', pt: 20, bgcolor: T.bg, minHeight: '100vh' }}>
      <CircularProgress size={20} sx={{ color: T.outlineVariant }} />
    </Box>
  );

  /* ── Derived data ──────────────────────────────────────────────────────── */
  const weekNr = getWeekNumber(new Date()); // FE-08: ISO-8601, was op 156 van 336 dagen fout
  const allToday = [...overdueTasks, ...todayTasks];
  const taskCount = allToday.length || weekTasks.length;
  const doneCount = (allToday.length > 0 ? allToday : weekTasks).filter(t => t.f.Afgerond).length;
  // Filter draaidagen: alleen waar gebruiker PL is of in crew zit
  const ddAll = Array.isArray(draaidagen) ? draaidagen : [];
  const ddList = ddAll.filter(d => {
    const plEmails = d.plEmail || d.fields?.plEmail || [];
    const crewEmails = d.crewEmail || d.fields?.crewEmail || [];
    const allEmails = [...plEmails, ...crewEmails].map(e => e?.toLowerCase());
    return allEmails.includes(userEmail.toLowerCase());
  });
  // Filter out Vanallesuurtje — niet relevant voor cockpit overview
  const filteredToday = allToday.filter(t => t.f.PrioNr !== 'Vanallesuurtje');
  const filteredWeek = weekTasks.filter(t => t.f.PrioNr !== 'Vanallesuurtje');
  const tasks = filteredToday.length > 0 ? filteredToday.slice(0, 8) : filteredWeek.slice(0, 8);

  // Status colors for projects
  const statusColors = { 'In progress': T.primary, 'On Hold': '#d4943a', 'Afgerond': T.emerald };

  return (
    <Box sx={{ bgcolor: T.bg, minHeight: 'calc(100vh - 44px)' }}>
      <Box sx={{ maxWidth: 1600, mx: 'auto', px: { xs: 2, sm: 4, md: 5 }, py: { xs: 3, md: 5 } }}>

        {/* ── Welcome Header ─────────────────────────────────────────────── */}
        <Box sx={{
          display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-end',
          mb: { xs: 4, md: 6 }, gap: 2,
        }}>
          <Box>
            <Typography sx={{
              fontFamily: T.font, fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.75rem' },
              fontWeight: 800, color: T.text, letterSpacing: '-0.03em', lineHeight: 1.1,
            }}>
              {greet()}, {voornaam || userName?.split(' ')[0] || 'collega'}
            </Typography>
            <Typography sx={{
              fontFamily: T.fontBody, fontSize: '0.92rem', color: T.secondary, mt: 1, lineHeight: 1.6, maxWidth: 560,
            }}>
              {ddList.length > 0
                ? <>Je studio heeft <strong style={{ color: T.text }}>{ddList.length} draaidag{ddList.length !== 1 ? 'en' : ''}</strong> gepland. </>
                : ''}
              {attention.overdue > 0
                ? <><strong style={{ color: '#dc2626' }}>{attention.overdue} achterstallige taken</strong> vragen om aandacht. </>
                : taskCount > 0
                  ? <>{doneCount > 0 ? `Al ${doneCount} van ${taskCount} taken afgerond. ` : `${taskCount} taken staan klaar. `}</>
                  : 'Geen openstaande taken vandaag. '}
              {meetings.length > 0 && <>{meetings.length} meeting{meetings.length !== 1 ? 's' : ''} te verwerken.</>}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75 }}>
              <Icon name="calendar_today" size={16} sx={{ color: T.outlineVariant }} />
              <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.75rem', fontWeight: 500, color: T.outlineVariant }}>
                Full Frame Studio &bull; Week {weekNr}
              </Typography>
            </Box>
          </Box>
          {/* AI Insight — top right */}
          <Box sx={{
            bgcolor: 'rgba(99,89,131,0.06)', border: '1px solid rgba(99,89,131,0.12)',
            borderRadius: '0.75rem', p: 2.5, maxWidth: 320, flexShrink: 0,
            display: { xs: 'none', md: 'block' },
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Icon name="auto_awesome" fill size={18} sx={{ color: T.tertiary }} />
              <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.62rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: T.tertiary }}>
                AI Insight
              </Typography>
            </Box>
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.82rem', color: T.text, lineHeight: 1.5, mb: 1.5 }}>
              {attention.overdue > 0
                ? `${attention.overdue} achterstallige taken vergen aandacht. Overweeg herprioritering voor vandaag.`
                : taskCount > 0
                  ? `${taskCount} taken deze week. ${doneCount > 0 ? `${Math.round((doneCount/taskCount)*100)}% afgerond — goed op schema.` : 'Begin met de hoogste prioriteit.'}`
                  : 'Alles bijgewerkt. Goed moment om vooruit te plannen.'
              }
            </Typography>
            <Box
              onClick={() => navigate('/dashboard')}
              sx={{
                display: 'inline-flex', px: 2, py: 0.75, borderRadius: '0.4rem',
                bgcolor: T.tertiary, color: '#fff', cursor: 'pointer',
                fontFamily: T.fontBody, fontSize: '0.72rem', fontWeight: 700,
                transition: 'opacity 0.15s', '&:hover': { opacity: 0.85 },
              }}
            >
              Taken bekijken
            </Box>
          </Box>
        </Box>

        {/* ── Metric Cards ───────────────────────────────────────────────── */}
        <Box sx={{
          display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
          gap: { xs: 2, md: 3 }, mb: { xs: 4, md: 6 },
        }}>
          {/* Active Projects */}
          <Box onClick={() => navigate('/projects')} sx={{ bgcolor: T.surface, p: 3, borderRadius: '0.75rem', boxShadow: T.shadow, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', cursor: 'pointer', transition: 'all 0.15s', '&:hover': { boxShadow: '0 10px 30px rgba(45,51,53,0.1)', transform: 'translateY(-2px)' } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
              <Box sx={{ width: 40, height: 40, borderRadius: '0.6rem', bgcolor: T.primaryContainer, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Icon name="folder_managed" size={22} sx={{ color: T.primary }} />
              </Box>
              {ddList.length > 0 && (
                <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.68rem', fontWeight: 700, color: T.emerald }}>
                  +{ddList.length} draaidag{ddList.length !== 1 ? 'en' : ''}
                </Typography>
              )}
            </Box>
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: T.outline, mb: 0.5 }}>
              Actieve Projecten
            </Typography>
            <Typography sx={{ fontFamily: T.font, fontSize: '2rem', fontWeight: 800, color: T.text, mb: 1.5 }}>
              {activeProjects.length}
            </Typography>
            <LinearProgress variant="determinate" value={Math.min(activeProjects.length * 8, 100)} sx={{
              height: 4, borderRadius: 4, bgcolor: 'rgba(82,96,117,0.08)',
              '& .MuiLinearProgress-bar': { bgcolor: T.primary, borderRadius: 4 },
            }} />
          </Box>

          {/* Tasks */}
          <Box onClick={() => navigate('/dashboard')} sx={{ bgcolor: T.surface, p: 3, borderRadius: '0.75rem', boxShadow: T.shadow, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', cursor: 'pointer', transition: 'all 0.15s', '&:hover': { boxShadow: '0 10px 30px rgba(45,51,53,0.1)', transform: 'translateY(-2px)' } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
              <Box sx={{ width: 40, height: 40, borderRadius: '0.6rem', bgcolor: T.tertiaryContainer, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Icon name="task_alt" size={22} sx={{ color: T.tertiary }} />
              </Box>
              <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.68rem', fontWeight: 700, color: T.tertiary }}>
                {taskCount > 0 ? Math.round((doneCount / taskCount) * 100) : 0}% Done
              </Typography>
            </Box>
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: T.outline, mb: 0.5 }}>
              Task Status
            </Typography>
            <Typography sx={{ fontFamily: T.font, fontSize: '2rem', fontWeight: 800, color: T.text, mb: 1.5 }}>
              {doneCount}/{taskCount}
            </Typography>
            <LinearProgress variant="determinate" value={taskCount > 0 ? (doneCount / taskCount) * 100 : 0} sx={{
              height: 4, borderRadius: 4, bgcolor: 'rgba(99,89,131,0.1)',
              '& .MuiLinearProgress-bar': { bgcolor: T.tertiary, borderRadius: 4 },
            }} />
          </Box>

          {/* Draaidagen */}
          <Box onClick={() => navigate('/planning')} sx={{ bgcolor: T.surface, p: 3, borderRadius: '0.75rem', boxShadow: T.shadow, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', cursor: 'pointer', transition: 'all 0.15s', '&:hover': { boxShadow: '0 10px 30px rgba(45,51,53,0.1)', transform: 'translateY(-2px)' } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
              <Box sx={{ width: 40, height: 40, borderRadius: '0.6rem', bgcolor: '#dbeafe', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Icon name="videocam" size={22} sx={{ color: '#3b82f6' }} />
              </Box>
              {ddList.length > 0 && (
                <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.68rem', fontWeight: 700, color: T.emerald }}>
                  ↑ {ddList.length} komend
                </Typography>
              )}
            </Box>
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: T.outline, mb: 0.5 }}>
              Draaidagen
            </Typography>
            <Typography sx={{ fontFamily: T.font, fontSize: '2rem', fontWeight: 800, color: T.text, mb: 1.5 }}>
              {ddList.length}
            </Typography>
            <LinearProgress variant="determinate" value={Math.min(ddList.length * 15, 100)} sx={{
              height: 4, borderRadius: 4, bgcolor: 'rgba(59,130,246,0.08)',
              '& .MuiLinearProgress-bar': { bgcolor: '#3b82f6', borderRadius: 4 },
            }} />
          </Box>

          {/* Attention */}
          <Box sx={{ bgcolor: T.surface, p: 3, borderRadius: '0.75rem', boxShadow: T.shadow, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
              <Box sx={{
                width: 40, height: 40, borderRadius: '0.6rem',
                bgcolor: attention.overdue > 0 ? '#fee2e2' : T.emeraldBg,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Icon name={attention.overdue > 0 ? 'warning' : 'check_circle'} size={22}
                  sx={{ color: attention.overdue > 0 ? '#dc2626' : T.emerald }} />
              </Box>
              {attention.overdue > 0 && (
                <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.68rem', fontWeight: 700, color: '#dc2626' }}>
                  {attention.overdue} overdue
                </Typography>
              )}
            </Box>
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: T.outline, mb: 0.5 }}>
              Aandacht Nodig
            </Typography>
            <Typography sx={{ fontFamily: T.font, fontSize: '2rem', fontWeight: 800, color: T.text, mb: 1.5 }}>
              {attention.total || 0}
            </Typography>
            <LinearProgress variant="determinate" value={Math.min((attention.total || 0) * 20, 100)} sx={{
              height: 4, borderRadius: 4, bgcolor: attention.overdue > 0 ? 'rgba(220,38,38,0.08)' : 'rgba(16,185,129,0.1)',
              '& .MuiLinearProgress-bar': { bgcolor: attention.overdue > 0 ? '#dc2626' : T.emerald, borderRadius: 4 },
            }} />
          </Box>
        </Box>

        {/* ── Shortcuts ──────────────────────────────────────────────────── */}
        <Shortcuts />

        {/* ── Main Layout: Content + Sidebar ─────────────────────────────── */}
        <Box sx={{
          display: { xs: 'block', lg: 'flex' },
          gap: { lg: 5 },
        }}>

          {/* ── LEFT: Roadmap + Activity Feed ────────────────────────────── */}
          <Box sx={{ flex: 1, minWidth: 0 }}>

            {/* Production Roadmap */}
            {activeProjects.length > 0 && (
              <Box sx={{ mb: 5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography sx={{ fontFamily: T.font, fontSize: '1.2rem', fontWeight: 700, color: T.text }}>
                    Production Roadmap
                  </Typography>
                  <Typography
                    onClick={() => navigate('/projects')}
                    sx={{
                      fontFamily: T.fontBody, fontSize: '0.7rem', fontWeight: 700,
                      color: T.primary, cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.06em',
                      '&:hover': { textDecoration: 'underline' },
                    }}
                  >
                    Alle projecten
                  </Typography>
                </Box>

                {/* Roadmap cards */}
                <Box sx={{
                  bgcolor: T.surfaceLow, borderRadius: '0.75rem', p: 0.5, overflow: 'hidden',
                }}>
                  <Box sx={{ bgcolor: T.surface, borderRadius: '0.5rem', p: { xs: 2, md: 3 } }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      {activeProjects.slice(0, 5).map((p) => {
                        const f = p.fields || {};
                        const status = f.Status || '';
                        const barColor = statusColors[status] || T.outlineVariant;
                        const isActive = status === 'In progress';
                        // Simulate progress width based on status
                        const barWidth = status === 'In progress' ? '65%' : status === 'Afgerond' ? '100%' : '30%';

                        return (
                          <Box key={p.id} onClick={() => navigate(`/projects/${p.id}`)} sx={{
                            display: 'grid', gridTemplateColumns: { xs: '1fr', md: '280px 1fr' },
                            alignItems: 'center', gap: 2, cursor: 'pointer',
                            '&:hover .bar-fill': { opacity: 0.9 },
                          }}>
                            {/* Project info */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Box sx={{
                                width: 48, height: 48, borderRadius: '0.5rem',
                                bgcolor: T.surfaceLow, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                flexShrink: 0,
                              }}>
                                <Icon name="movie" size={22} sx={{ color: T.outline }} />
                              </Box>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography noWrap sx={{ fontFamily: T.fontBody, fontSize: '0.85rem', fontWeight: 700, color: T.text }}>
                                  {f.Projectnaam || 'Project'}
                                </Typography>
                                <Typography sx={{
                                  fontFamily: T.fontBody, fontSize: '0.6rem', fontWeight: 700,
                                  textTransform: 'uppercase',
                                  color: isActive ? T.emerald : T.outline,
                                }}>
                                  {status}
                                </Typography>
                              </Box>
                            </Box>

                            {/* Progress bar */}
                            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                              {isActive ? (
                                <Box sx={{
                                  height: 32, bgcolor: `${barColor}10`, borderRadius: '9999px',
                                  position: 'relative', overflow: 'hidden',
                                }}>
                                  <Box className="bar-fill" sx={{
                                    position: 'absolute', left: 0, top: 0, bottom: 0,
                                    width: barWidth, bgcolor: barColor, borderRadius: '9999px',
                                    display: 'flex', alignItems: 'center', px: 2,
                                    transition: 'opacity 0.2s',
                                  }}>
                                    <Typography sx={{
                                      fontFamily: T.fontBody, fontSize: '0.6rem', fontWeight: 700,
                                      color: '#fff', whiteSpace: 'nowrap',
                                    }}>
                                      {f.Projectnummer || ''}
                                    </Typography>
                                  </Box>
                                </Box>
                              ) : (
                                <Box sx={{
                                  height: 32, border: '2px dashed', borderColor: 'rgba(173,179,181,0.3)',
                                  borderRadius: '9999px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                  <Typography sx={{
                                    fontFamily: T.fontBody, fontSize: '0.6rem', fontWeight: 700, color: T.outline,
                                  }}>
                                    {status || 'Gepland'}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                </Box>
              </Box>
            )}

            {/* Activity Feed — Tasks */}
            <Box sx={{ mb: 5 }}>
              <Typography sx={{
                fontFamily: T.fontBody, fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.08em',
                textTransform: 'uppercase', color: T.outline, mb: 2,
              }}>
                {allToday.length > 0 ? 'Taken Vandaag' : 'Taken Deze Week'}
              </Typography>

              {tasks.length === 0 ? (
                <Box sx={{
                  bgcolor: T.surfaceLow, p: 4, borderRadius: '0.75rem', textAlign: 'center',
                }}>
                  <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.85rem', color: T.outline }}>
                    Geen taken ingepland.
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {tasks.map((t) => {
                    // Draaidag of vrije dag is nooit overdue (Joris, 3 nov 2026).
                    const od = t.f.Deadline && dUntil(t.f.Deadline) < 0 && !isDagstaatTaak(t.f);
                    const done = !!t.f.Afgerond;
                    const proj = t.f['Prnr en Naam']?.[0] || '';
                    const prio = t.f.PrioNr || '';

                    // Color coding per task type
                    const taskStyle = done
                      ? { icon: 'check_circle', color: T.emerald, bg: T.emeraldBg, border: T.emerald }
                      : od
                        ? { icon: 'error', color: '#dc2626', bg: '#fef2f2', border: '#dc2626' }
                      : prio === 'Afspraak'
                        ? { icon: 'event', color: '#3b82f6', bg: '#dbeafe', border: '#3b82f6' }
                      : prio === 'Draaidag'
                        ? { icon: 'videocam', color: '#f59e0b', bg: '#fef3c7', border: '#f59e0b' }
                      : prio === 'Vanallesuurtje'
                        ? { icon: 'widgets', color: T.tertiary, bg: T.tertiaryContainer, border: T.tertiary }
                      : prio.includes('Prio 1')
                        ? { icon: 'priority_high', color: '#dc2626', bg: '#fef2f2', border: '#dc2626' }
                      : prio.includes('Prio 2')
                        ? { icon: 'arrow_upward', color: '#ea580c', bg: '#fff7ed', border: '#ea580c' }
                      : prio === 'Deadline'
                        ? { icon: 'schedule', color: '#dc2626', bg: '#fef2f2', border: '#dc2626' }
                        : { icon: 'task_alt', color: T.primary, bg: T.primaryContainer, border: T.primary };

                    return (
                      <Box key={t.id} onClick={() => toggle(t)} sx={{
                        bgcolor: T.surface, p: 2, borderRadius: '0.75rem',
                        display: 'flex', alignItems: 'center', gap: 2,
                        cursor: 'pointer', transition: 'all 0.15s',
                        opacity: done ? 0.45 : 1,
                        borderLeft: `4px solid ${taskStyle.border}`,
                        boxShadow: '0 1px 3px rgba(45,51,53,0.04)',
                        '&:hover': { boxShadow: '0 4px 12px rgba(45,51,53,0.08)', transform: 'translateX(2px)' },
                      }}>
                        <Box sx={{
                          width: 38, height: 38, borderRadius: '0.5rem', bgcolor: taskStyle.bg,
                          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                        }}>
                          <Icon name={taskStyle.icon} size={20} sx={{ color: taskStyle.color }} />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography noWrap sx={{
                            fontFamily: T.fontBody, fontSize: '0.85rem', fontWeight: 600, color: T.text,
                            textDecoration: done ? 'line-through' : 'none',
                          }}>
                            {t.f.NaamTaak || 'Taak'}
                          </Typography>
                          <Typography noWrap sx={{ fontFamily: T.fontBody, fontSize: '0.68rem', color: T.outline, mt: 0.15 }}>
                            {prio && <Box component="span" sx={{ color: taskStyle.color, fontWeight: 600 }}>{prio}</Box>}
                            {prio && proj ? ' • ' : ''}{proj}
                          </Typography>
                        </Box>
                        {od && (
                          <Typography sx={{
                            fontFamily: T.fontBody, fontSize: '0.6rem', fontWeight: 700,
                            color: '#fff', bgcolor: '#dc2626', px: 1, py: 0.3, borderRadius: '0.3rem',
                            textTransform: 'uppercase', flexShrink: 0,
                          }}>
                            Overdue
                          </Typography>
                        )}
                        {!od && t.f.Deadline && (
                          <Typography sx={{
                            fontFamily: T.fontBody, fontSize: '0.68rem', color: T.outlineVariant, flexShrink: 0,
                          }}>
                            {fmtWeekday(t.f.Deadline)}
                          </Typography>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>

            {/* Feedback section */}
            {feedback.length > 0 && (
              <Box sx={{ mb: 5 }}>
                <Typography sx={{
                  fontFamily: T.fontBody, fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.08em',
                  textTransform: 'uppercase', color: T.outline, mb: 2,
                }}>
                  Recente Feedback
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {feedback.slice(0, 3).map((fb, i) => (
                    <Box key={fb.id || i} onClick={() => navigate('/my-feedback')} sx={{
                      bgcolor: T.surfaceLow, p: 2, borderRadius: '0.75rem',
                      display: 'flex', alignItems: 'flex-start', gap: 2,
                      cursor: 'pointer', '&:hover': { bgcolor: T.surfaceHigh },
                    }}>
                      <Box sx={{
                        p: 1, borderRadius: '0.5rem', bgcolor: `${T.tertiary}10`,
                        display: 'flex', flexShrink: 0,
                      }}>
                        <Icon name="feedback" size={20} sx={{ color: T.tertiary }} />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography noWrap sx={{ fontFamily: T.fontBody, fontSize: '0.85rem', color: T.text }}>
                          {fb.description || fb.tool_name || 'Feedback'}
                        </Typography>
                        <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.62rem', color: T.outline, mt: 0.25 }}>
                          {fb.pipeline_status || 'nieuw'}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>

          {/* ── RIGHT SIDEBAR ────────────────────────────────────────────── */}
          <Box sx={{
            width: { xs: '100%', lg: 380 }, flexShrink: 0,
            mt: { xs: 4, lg: 0 }, display: 'flex', flexDirection: 'column', gap: 3,
          }}>

            {/* Agenda / Meetings */}
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography sx={{ fontFamily: T.font, fontSize: '1.1rem', fontWeight: 700, color: T.text }}>
                  Agenda
                </Typography>
                <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.6rem', fontWeight: 700, color: T.outline, textTransform: 'uppercase' }}>
                  Deze week
                </Typography>
              </Box>

              <Box sx={{ bgcolor: T.surfaceLow, borderRadius: '0.75rem', p: 3, boxShadow: T.shadow }}>
                {meetings.length === 0 && ddList.length === 0 ? (
                  <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.82rem', color: T.outline }}>
                    Geen afspraken.
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {/* Meetings */}
                    {meetings.length > 0 && (
                      <>
                        <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.58rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: T.outlineVariant, mb: 0.5, mt: 0.5 }}>
                          Meetings te verwerken
                        </Typography>
                        {meetings.slice(0, 5).map((m, i) => (
                          <Box key={m.id || i} onClick={() => navigate('/MeetingDashboard')} sx={{
                            display: 'flex', alignItems: 'center', gap: 1.5,
                            bgcolor: T.surface, borderRadius: '0.5rem', p: 1.5,
                            cursor: 'pointer', borderLeft: `3px solid ${m.status === 'new' ? T.emerald : '#3b82f6'}`,
                            transition: 'all 0.15s',
                            '&:hover': { boxShadow: '0 2px 6px rgba(45,51,53,0.06)' },
                          }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.82rem', fontWeight: 600, color: T.text, lineHeight: 1.3 }}>
                                {m.title || 'Meeting'}
                              </Typography>
                              <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.68rem', color: T.outline, mt: 0.25 }}>
                                {m.meeting_date ? fmtWeekday(m.meeting_date) : ''}
                              </Typography>
                            </Box>
                            {m.status === 'new' && (
                              <Typography sx={{
                                fontFamily: T.fontBody, fontSize: '0.55rem', fontWeight: 700,
                                color: T.emerald, bgcolor: T.emeraldBg, px: 1, py: 0.3, borderRadius: '9999px', flexShrink: 0,
                              }}>
                                NIEUW
                              </Typography>
                            )}
                          </Box>
                        ))}
                      </>
                    )}

                    {/* Draaidagen */}
                    {ddList.length > 0 && (
                      <>
                        <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.58rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: T.outlineVariant, mb: 0.5, mt: meetings.length > 0 ? 1.5 : 0.5 }}>
                          Draaidagen
                        </Typography>
                        {ddList.slice(0, 4).map((d, i) => (
                          <Box key={d.id || i} onClick={() => navigate('/planning')} sx={{
                            display: 'flex', alignItems: 'center', gap: 1.5,
                            bgcolor: T.surface, borderRadius: '0.5rem', p: 1.5,
                            cursor: 'pointer', borderLeft: '3px solid #f59e0b',
                            transition: 'all 0.15s',
                            '&:hover': { boxShadow: '0 2px 6px rgba(45,51,53,0.06)' },
                          }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.82rem', fontWeight: 600, color: T.text, lineHeight: 1.3 }}>
                                {d.fields?.Naam || d.naam || d.name || 'Draaidag'}
                              </Typography>
                              <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.68rem', color: '#f59e0b', fontWeight: 600, mt: 0.25 }}>
                                {fmtWeekday(d.fields?.Datum || d.datum || d.date)}
                              </Typography>
                            </Box>
                            <Typography sx={{
                              fontFamily: T.fontBody, fontSize: '0.55rem', fontWeight: 700,
                              color: '#f59e0b', bgcolor: '#fef3c7', px: 1, py: 0.3, borderRadius: '9999px', flexShrink: 0,
                            }}>
                              SHOOT
                            </Typography>
                          </Box>
                        ))}
                      </>
                    )}
                  </Box>
                )}
              </Box>
            </Box>

            {/* Deadlines */}
            {deadlines.length > 0 && (
              <Box sx={{ bgcolor: T.surfaceLow, borderRadius: '0.75rem', p: 3, boxShadow: T.shadow }}>
                <Typography sx={{
                  fontFamily: T.fontBody, fontSize: '0.7rem', fontWeight: 700,
                  letterSpacing: '0.08em', textTransform: 'uppercase', color: T.outline, mb: 2,
                }}>
                  Deadlines
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {deadlines.slice(0, 4).map((d, i) => {
                    const ds = d.deadline || d.Deadline;
                    const days = dUntil(ds);
                    const urg = days !== null && days <= 1;
                    return (
                      <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{
                          width: 36, height: 36, borderRadius: '50%', bgcolor: urg ? 'rgba(168,56,54,0.08)' : T.surface,
                          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                        }}>
                          <Icon name={urg ? 'schedule' : 'event_upcoming'} size={18} sx={{ color: urg ? T.error : T.outline }} />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography noWrap sx={{
                            fontFamily: T.fontBody, fontSize: '0.82rem', fontWeight: urg ? 700 : 500, color: urg ? T.error : T.text,
                          }}>
                            {d.name || d.NaamTaak || ''}
                          </Typography>
                          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.62rem', color: T.outline }}>
                            {fmtWeekday(ds)}{days !== null && days <= 0 ? ' — overdue' : ''}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}

            {/* Create Project button — Stitch sidebar style */}
            <Box
              onClick={() => navigate('/projects')}
              sx={{
                background: `linear-gradient(135deg, ${T.tertiary} 0%, ${T.tertiaryDim} 100%)`,
                borderRadius: '0.75rem', p: 2, color: '#fff',
                boxShadow: '0 6px 20px rgba(99,89,131,0.25)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1,
                cursor: 'pointer', fontFamily: T.fontBody, fontWeight: 700, fontSize: '0.85rem',
                transition: 'all 0.2s', '&:hover': { boxShadow: '0 8px 28px rgba(99,89,131,0.35)' },
                '&:active': { transform: 'scale(0.97)' },
              }}
            >
              <Icon name="add" size={20} sx={{ color: '#fff' }} />
              Nieuw Project
            </Box>

          </Box>
        </Box>
      </Box>
    </Box>
  );
}
